// Angular imports
import { HttpClient } from '@angular/common/http';
// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';
// RxJs imports
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';
import { WFEWorkflowRawDto } from '../../shared-workflow-module/_models/wfe-workflow-raw-dto';
import { WorkflowHistoryResultDto } from '../../shared-workflow-module/_models/wfe-workflow-history-result-dto';
import { StatusMessageResultDto } from '../../_models/status-message-result-dto';
import { WFEVendorTemplateResultDto } from '../_models/workflow-data-entry/wfe-vendor-template-result-dto';
import { WFEVendorAccountTemplateResultDto } from '../_models/workflow-data-entry/wfe-vendor-account-template-result-dto';
import { WFVendorNotesResultDto } from '../../shared-workflow-module/_models/wf-vendor-notes-result-dto';
import { WFEBankBalanceResultDto } from '../_models/common/wfe-bank-balance-result-dto';
import { WFEOwnerResultDto } from '../../shared-workflow-module/_models/wfe-owner-result-dto';
import { WFEVendorResultDto } from '../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { WFEVendorAccountResultDto } from '../../shared-workflow-module/_models/wfe-vendor-account-result-dto';
import { WFECommunityResultDto } from '../../shared-workflow-module/_models/wfe-community-result-dto';
// constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../../_services/shared-services/mp-cache-registration-service";
export class WorkflowEditorService {
    constructor(httpClient, cacheRegistrationService) {
        this.httpClient = httpClient;
        this.cacheRegistrationService = cacheRegistrationService;
        this._subjectRefreshWorkflow = new Subject();
    }
    dataEntryEditor(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}wf/dataentry${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(WFEWorkflowRawDto, response)));
    }
    nextDataEntryEditor() {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}next/dataentry`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(WFEWorkflowRawDto, response)));
    }
    downloadWorkflowInvoice(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}downloadinvoice${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData) => new Blob([responseData], { type: 'application/pdf' })));
    }
    getWorkflowEditorCommunities(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}communities${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(WFECommunityResultDto, response)));
    }
    getWorkflowEditorOwners(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}owners${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.tenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFEOwnerResultDto, response)));
    }
    getWorkflowEditorVendors(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendors${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.tenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFEVendorResultDto, response)));
    }
    getWorkflowVendorAccount(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/accounts${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFEVendorAccountResultDto, response)));
    }
    getWorkflowHistory(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}wfhistory${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WorkflowHistoryResultDto, response)));
    }
    closeWorkflowEditor(baseParams) {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}unlock`, classToPlain(baseParams))
            .pipe(map((responseData) => plainToClass(StatusMessageResultDto, responseData)));
    }
    getVendorNotes(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/notes${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFVendorNotesResultDto, response)));
    }
    createCommunityVendor(baseParams) {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/communityvendor`, classToPlain(baseParams))
            .pipe(map((responseData) => responseData));
    }
    transferWorkQueue(baseParams) {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowEditorBaseApiUrl}transferworkqueue`, classToPlain(baseParams))
            .pipe(map((responseData) => plainToClass(StatusMessageResultDto, responseData)));
    }
    deleteWorkflow(baseParams) {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}delete`, classToPlain(baseParams))
            .pipe(map((responseData) => plainToClass(StatusMessageResultDto, responseData)));
    }
    replaceInvoice(baseParams) {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}replaceinvoice`, classToPlain(baseParams))
            .pipe(map((responseData) => plainToClass(StatusMessageResultDto, responseData)));
    }
    getVendorLineTemplate(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/templates${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFEVendorTemplateResultDto, response)));
    }
    getVendorAccountLineTemplate(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/account/templates${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFEVendorAccountTemplateResultDto, response)));
    }
    sendWorkflowToVendorEdit(baseParams) {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}vendoredit`, classToPlain(baseParams))
            .pipe(map((responseData) => plainToClass(StatusMessageResultDto, responseData)));
    }
    getWorkflowCommunityBillAddress(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}billtoaddress${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient.get(url, options).pipe(map((response) => response.toString()));
    }
    verifyAndSendWorkflowToSupervisor(baseParams) {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}supervisor`, classToPlain(baseParams))
            .pipe(map((responseData) => plainToClass(StatusMessageResultDto, responseData)));
    }
    verifyAndApproveWorkflow(baseParams) {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}verifyapprove`, classToPlain(baseParams))
            .pipe(map((responseData) => plainToClass(StatusMessageResultDto, responseData)));
    }
    getSubjectRefreshWorkflow() {
        return this._subjectRefreshWorkflow;
    }
    updateSubjectRefreshWorkflow(value) {
        this._subjectRefreshWorkflow.next(value);
    }
    getBankBalance(baseParams) {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}bankbalance${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url);
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(WFEBankBalanceResultDto, response)));
    }
    manageWorkOrders(baseParams, step) {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowEditorBaseApiUrl}${step}/workorders/manage`, classToPlain(baseParams))
            .pipe(map((responseData) => responseData));
    }
    attributeCommunity(baseParams) {
        return this.httpClient.put(`${URLConstants.ccWorkflowEditorBaseApiUrl}attribute-community`, classToPlain(baseParams));
    }
    saveWorkflowVendorContracts(baseParams) {
        return this.httpClient
            .put(`${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/contracts`, classToPlain(baseParams))
            .pipe(map((responseData) => responseData));
    }
}
WorkflowEditorService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function WorkflowEditorService_Factory() { return new WorkflowEditorService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.MPCacheRegistrationService)); }, token: WorkflowEditorService, providedIn: "root" });
